import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { db } from "../firebase/firebase";

import "./CategoryGallery.css";

function CategoryGallery() {
  const { type, category } = useParams();

  const { t, i18n } = useTranslation();

  // =====================================================
  // DECODE URL
  // =====================================================

  const decodedType = decodeURIComponent(type || "");
  const decodedCategory = decodeURIComponent(category || "");

  // =====================================================
  // STATE
  // =====================================================

  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);

  // =====================================================
  // LANGUAGE
  // =====================================================

  const isArabic = i18n.language === "ar";

  // =====================================================
  // CATEGORY TRANSLATION
  // =====================================================

  const getCategoryName = (categoryName) => {
    const categoryTranslations = {
      Weddings: t(
        "photography.categories.weddings.name"
      ),

      "Islamic Weddings": t(
        "photography.categories.islamicWeddings.name"
      ),

      Engagements: t(
        "photography.categories.engagements.name"
      ),

      "Bridal Sessions": t(
        "photography.categories.bridalSessions.name"
      ),

      "Women Events": t(
        "photography.categories.womenEvents.name"
      ),

      "Family & Friends": t(
        "photography.categories.familyFriends.name"
      ),

      "Bridal Robes": t(
        "rental.categories.bridalRobes.name"
      ),

      "Bridesmaids Robe": t(
        "rental.categories.bridesmaids.name"
      ),

      "Bridesmaids Robes": t(
        "rental.categories.bridesmaids.name"
      ),

      Accessories: t(
        "rental.categories.accessories.name"
      ),
    };

    return (
      categoryTranslations[categoryName] ||
      categoryName
    );
  };

  // =====================================================
  // TYPE TRANSLATION
  // =====================================================

  const getTypeName = (typeName) => {
    if (!typeName) {
      return "";
    }

    if (
      typeName.toLowerCase() === "photography"
    ) {
      return t("nav.photography");
    }

    if (
      typeName.toLowerCase() === "rental"
    ) {
      return t("nav.rental");
    }

    return typeName;
  };

  // =====================================================
  // LOAD GALLERY
  // =====================================================

  useEffect(() => {
    const loadGallery = async () => {
      try {
        setLoading(true);

        const galleryRef = collection(
          db,
          "gallery"
        );

        const galleryQuery = query(
          galleryRef,
          where(
            "type",
            "==",
            decodedType
          ),
          where(
            "category",
            "==",
            decodedCategory
          )
        );

        const snapshot =
          await getDocs(galleryQuery);

        const galleryData =
          snapshot.docs.map((item) => ({
            id: item.id,
            ...item.data(),
          }));

        setAlbums(galleryData);

      } catch (error) {
        console.error(
          "CATEGORY GALLERY ERROR:",
          error
        );

        setAlbums([]);

      } finally {
        setLoading(false);
      }
    };

    if (
      decodedType &&
      decodedCategory
    ) {
      loadGallery();
    } else {
      setAlbums([]);
      setLoading(false);
    }

  }, [
    decodedType,
    decodedCategory,
  ]);

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <main className="category-gallery-page">

        <div className="gallery-message">

          <div className="gallery-loading-symbol">
            ✦
          </div>

          <p>
            {t(
              "categoryGallery.loading"
            )}
          </p>

        </div>

      </main>
    );
  }

  // =====================================================
  // GET COVER IMAGE
  // =====================================================

  const getCoverImage = (album) => {
    // Multiple images
    if (
      Array.isArray(album.images) &&
      album.images.length > 0
    ) {
      return album.images[0];
    }

    // Single image
    if (
      typeof album.image === "string" &&
      album.image.trim() !== ""
    ) {
      return album.image;
    }

    return "";
  };

  // =====================================================
  // GET PHOTO COUNT
  // =====================================================

  const getPhotoCount = (album) => {
    if (
      Array.isArray(album.images)
    ) {
      return album.images.length;
    }

    if (
      typeof album.image === "string" &&
      album.image.trim() !== ""
    ) {
      return 1;
    }

    return 0;
  };

  // =====================================================
  // BACK PATH
  // =====================================================

  const getBackPath = () => {
    if (
      decodedType.toLowerCase() ===
      "photography"
    ) {
      return "/photography";
    }

    if (
      decodedType.toLowerCase() ===
      "rental"
    ) {
      return "/rental";
    }

    return "/";
  };

  // =====================================================
  // CATEGORY TITLE
  // =====================================================

  const categoryTitle =
    getCategoryName(decodedCategory);

  // =====================================================
  // TYPE TITLE
  // =====================================================

  const typeTitle =
    getTypeName(decodedType);

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <main className="category-gallery-page">

      {/* =================================================
          HEADER
      ================================================= */}

      <section className="category-gallery-header">

        {/* TYPE */}

        <span className="category-gallery-type">
          {typeTitle}
        </span>

        {/* CATEGORY */}

        <h1>
          {categoryTitle}
        </h1>

        {/* DIVIDER */}

        <div className="category-gallery-divider">

          <span></span>

          <i>✦</i>

          <span></span>

        </div>

      </section>

      {/* =================================================
          EMPTY GALLERY
      ================================================= */}

      {albums.length === 0 ? (

        <div className="gallery-message">

          <div>

            <div
              className="gallery-empty-symbol"
              aria-hidden="true"
            >
              ✦
            </div>

            <h2>
              {t(
                "categoryGallery.noGalleries"
              )}
            </h2>

            <p>
              {t(
                "categoryGallery.waiting"
              )}
            </p>

            <Link
              to={getBackPath()}
              className="gallery-back-link"
            >
              <span>
                {isArabic ? "→" : "←"}
              </span>

              {t(
                "categoryGallery.back"
              )}
            </Link>

          </div>

        </div>

      ) : (

        /* =================================================
           GALLERY GRID
        ================================================= */

        <section className="collections-grid">

          {albums.map(
            (album, index) => {

              const coverImage =
                getCoverImage(album);

              const photoCount =
                getPhotoCount(album);

              const albumTitle =
                album.title ||
                t(
                  "categoryGallery.untitled"
                );

              return (

                <Link
                  key={album.id}
                  to={`/gallery/${encodeURIComponent(
                    decodedType
                  )}/${encodeURIComponent(
                    decodedCategory
                  )}/${album.id}`}
                  className="collection-card"
                >

                  {/* =================================================
                      IMAGE
                  ================================================= */}

                  <div className="collection-image">

                    {coverImage ? (

                      <img
                        src={coverImage}
                        alt={albumTitle}
                        loading="lazy"
                      />

                    ) : (

                      <div className="image-placeholder">
                        ✦
                      </div>

                    )}

                    {/* OVERLAY */}

                    <div className="category-gallery-overlay">

                      <span>
                        {t(
                          "categoryGallery.viewGallery"
                        )}

                        {" ↗"}
                      </span>

                    </div>

                  </div>

                  {/* =================================================
                      INFO
                  ================================================= */}

                  <div className="collection-info">

                    {/* NUMBER */}

                    <span className="collection-number">
                      {String(
                        index + 1
                      ).padStart(2, "0")}
                    </span>

                    {/* TITLE */}

                    <h2>
                      {albumTitle}
                    </h2>

                    {/* DESCRIPTION */}

                    {album.description && (
                      <p>
                        {album.description}
                      </p>
                    )}

                    {/* PHOTO COUNT */}

                    <small className="collection-photo-count">

                      {photoCount}

                      {" "}

                      {photoCount === 1
                        ? t(
                            "categoryGallery.photograph"
                          )
                        : t(
                            "categoryGallery.photographs"
                          )}

                    </small>

                    {/* ARROW */}

                    <span className="collection-arrow">
                      ↗
                    </span>

                  </div>

                </Link>

              );
            }
          )}

        </section>

      )}

    </main>
  );
}

export default CategoryGallery;